import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { ConsultationResponse } from '../../types/oracle';
import { formatTitle } from '../../utils/stringUtils';

interface ShareCardOption {
  id: string;
  type: 'sentence';
  corpus: 'hymns' | 'argonautica' | 'lithica';
  content: {
    text: string;
    lineCount: number;
    sectionTitle: string;
  };
  metadata: {
    sentenceId: number;
    incense?: {
      english: string;
      greek?: string;
    };
  };
}

interface ShareCardProps {
  response: ConsultationResponse;
  currentOption: ShareCardOption;
}

const ShareCard: React.FC<ShareCardProps> = React.memo(({ response, currentOption }) => {
  const cardId = 'share-card-render-target'; // ID for html-to-image
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [scale, setScale] = useState<number>(1);
  
  // Dynamic scaling based on window size - but keep it consistent for capture
  useEffect(() => {
    const updateScale = () => {
      const width = window.innerWidth;
      
      // Continuous scaling: 0.7 at 320px, 1.0 at 900px+
      const minWidth = 320;
      const maxWidth = 900;
      const minScale = 0.7;
      const maxScale = 1.0;
      
      let newScale = minScale + (maxScale - minScale) * 
        Math.min(Math.max((width - minWidth) / (maxWidth - minWidth), 0), 1);
      
      // Round to 2 decimal places for cleaner scaling
      newScale = Math.round(newScale * 100) / 100;
      
      setScale(newScale);
    };
    
    // Set initial scale
    updateScale();
    
    // Listen for window resize
    window.addEventListener('resize', updateScale);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // Capitalize first letter of sentence
  const capitalizeFirst = (text: string): string => {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  // Get line range from original response data
  const getLineRange = (corpus: string): string | null => {
    const selection = response.selections[corpus as keyof typeof response.selections];
    if (selection?.lineDetails && selection.lineDetails.length > 0) {
      if (selection.lineDetails.length === 1) {
        return `l. ${selection.lineDetails[0].line}`;
      } else {
        const firstLine = selection.lineDetails[0].line;
        const lastLine = selection.lineDetails[selection.lineDetails.length - 1].line;
        return `ll. ${firstLine}-${lastLine}`;
      }
    }
    return null;
  };

  // Generate QR code
  useEffect(() => {
    const generateQR = async () => {
      try {
        const url = await QRCode.toDataURL('https://cleros.gbe.games', {
          width: 80,
          margin: 1,
          color: {
            dark: '#333333',
            light: '#1a1a1a'
          }
        });
        setQrCodeUrl(url);
      } catch (error) {
        console.error('Failed to generate QR code:', error);
      }
    };
    generateQR();
  }, []);

  // Simple date formatter
  const formatTimestamp = (timestamp: number): string => {
    try {
      return new Date(timestamp).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
            } catch {
      return 'Invalid Date';
    }
  };





  // Prepare display data
  const incense = currentOption.metadata?.incense?.english;

  return (
    // ShareCard component for display and image capture
    <div
      id={cardId}
      data-testid="share-card-render-target"
      style={{
        width: `${500 * scale}px`,
        backgroundColor: '#242830', // Card background
        color: '#f5f4f0', // Primary text color
        padding: `${24 * scale}px`,
        borderRadius: `${8 * scale}px`,
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        textAlign: 'center' as const,
        boxShadow: `0 ${4 * scale}px ${20 * scale}px rgba(0,0,0,0.15)`,
        border: `${1 * scale}px solid #a8a8a833`,
        margin: '0 auto'
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: `${16 * scale}px` }}>
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: `${8 * scale}px`,
            marginBottom: `${8 * scale}px`
          }}
        >
          <h1 
            style={{ 
              color: '#f5f4f0',
              fontSize: `${24 * scale}px`,
              fontWeight: 500,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              margin: 0
            }}
          >
            cleros
          </h1>
          <span 
            style={{
              color: '#a8a8a8',
              fontSize: `${16 * scale}px`,
              fontWeight: 300,
              letterSpacing: '0.08em',
              opacity: 0.8
            }}
          >
            | digital bibliomancy
          </span>
        </div>
        <div 
          style={{
            height: `${1 * scale}px`,
            backgroundColor: '#a8a8a833',
            margin: `${16 * scale}px 0`
          }}
        />
      </div>

      {/* Query */}
      <div 
        style={{ 
          color: '#a8a8a8',
          fontStyle: 'italic',
          fontSize: `${14 * scale}px`,
          marginBottom: `${18 * scale}px`,
          opacity: 0.8,
          fontWeight: 300
        }}
      >
        "{response.query}"
      </div>

      {/* Content Header - Current selection title */}
      <div style={{ marginBottom: `${12 * scale}px` }}>
        {/* Top bar */}
        <div 
          style={{
            height: `${1 * scale}px`,
            backgroundColor: '#a8a8a833',
            width: '15%',
            minWidth: `${40 * scale}px`,
            maxWidth: `${60 * scale}px`,
            margin: `0 auto ${9 * scale}px auto`
          }}
        />
        <div 
          style={{ 
            color: '#a8a8a8',
            fontSize: `${13 * scale}px`,
            fontWeight: '500',
            lineHeight: 1.4,
            margin: `0 0 ${9 * scale}px 0`,
            letterSpacing: '0.02em'
          }}
        >
          {/* Corpus */}
          <span style={{ textTransform: 'uppercase', fontWeight: '500' }}>
            {currentOption.corpus}
          </span>
          <span style={{ margin: `0 ${8 * scale}px`, color: '#a8a8a8' }}>•</span>
          {/* Title and line info */}
          {currentOption.corpus === 'hymns' 
            ? formatTitle(currentOption.content.sectionTitle)
            : currentOption.content.sectionTitle}
          {getLineRange(currentOption.corpus) && (
            <>
              <span style={{ color: '#a8a8a8' }}>,</span>
              <span style={{ margin: `0 0 0 ${4 * scale}px`, color: '#a8a8a8' }}>
                {getLineRange(currentOption.corpus)}
              </span>
            </>
          )}
          {incense && (
            <>
              <span style={{ margin: `0 ${8 * scale}px` }}>•</span>
              <span style={{ fontWeight: 'normal' }}>{incense}</span>
            </>
          )}
        </div>
        {/* Bottom bar */}
        <div 
          style={{
            height: `${1 * scale}px`,
            backgroundColor: '#a8a8a833',
            width: '15%',
            minWidth: `${40 * scale}px`,
            maxWidth: `${60 * scale}px`,
            margin: `0 auto ${12 * scale}px auto`
          }}
        />
      </div>

      {/* Main Quote */}
       <div style={{ position: 'relative', marginBottom: `${18 * scale}px` }}>

         <div 
           style={{ 
             color: '#f0f0f0',
             fontSize: `${18 * scale}px`,
             lineHeight: 1.6,
             padding: `0 ${8 * scale}px`,
             wordWrap: 'break-word',
             overflowWrap: 'break-word',
             hyphens: 'auto',
             position: 'relative',
             zIndex: 2,
             marginBottom: `${24 * scale}px`,
             fontWeight: 400
           }}
         >
           {capitalizeFirst(currentOption.content.text)}
         </div>
       </div>



      {/* Footer */}
      <div style={{ marginTop: `${50 * scale}px`, position: 'relative' }}>
        <div 
          style={{
            position: 'absolute',
            bottom: `${25 * scale}px`,
            left: '15%',
            right: '15%',
            height: `${1 * scale}px`,
            backgroundColor: '#333'
          }}
        />
        {/* Orphic Egg - bottom left */}
        <div 
                      style={{
              position: 'absolute',
              bottom: `${-17.5 * scale}px`,
              left: `${-20 * scale}px`,
              width: `${85 * scale}px`,
              height: `${85 * scale}px`,
              opacity: 0.9,
              zIndex: 1
            }}
        >
          <img 
            src="/orphic-egg.png" 
            alt="Orphic Egg" 
            crossOrigin="anonymous"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
        
        <div 
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: `${8 * scale}px`,
            marginTop: `${24 * scale}px`
          }}
        >
          <div 
            style={{ 
              color: '#888888',
              fontSize: `${11 * scale}px`,
              fontWeight: 300
            }}
          >
            {formatTimestamp(response.timestamp)}
          </div>
          <div style={{ color: '#666', fontSize: `${11 * scale}px` }}>•</div>
          <div 
            style={{ 
              color: '#888888',
              fontSize: `${11 * scale}px`,
              fontWeight: 300
            }}
          >
            cleros.gbe.games
          </div>
        </div>
        {/* QR Code */}
        {qrCodeUrl && (
          <img
            src={qrCodeUrl}
            alt="QR Code for cleros.gbe.games"
            style={{
              position: 'absolute',
              bottom: '0px',
              right: '0px',
              width: `${50 * scale}px`,
              height: `${50 * scale}px`,
              opacity: 0.8
            }}
          />
        )}
      </div>
    </div>
  );
});

ShareCard.displayName = 'ShareCard';

export default ShareCard;
